import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import {
  searchMatters,
  getSearchMattersNextPageNumber,
  type MatterSearchResult,
  type SearchMattersInput,
} from "@/lib/data/requests/matters/search-matters";
import type { APIResponse, ReportsOpenMattersReport } from "@/lib/types";

export type RetrieveReportsOpenMattersReportInput =
  | SearchMattersInput
  | undefined;

export const retrieveReportsOpenMattersReportV1 = async (
  data: RetrieveReportsOpenMattersReportInput = {},
  signal?: AbortSignal,
) => {
  const matters: MatterSearchResult[] = [];
  let pageNumber: number | undefined = 1;
  let meta: APIResponse<ReportsOpenMattersReport[]>["meta"] = { userMsg: "" };
  while (pageNumber !== undefined) {
    const page = await searchMatters(
      {
        ...data,
        statusCategories: ["pre-litigation", "litigation"],
        pageNumber,
        pageSize: 250,
      },
      signal,
    );
    matters.push(...page.data);
    meta = page.meta;
    pageNumber = getSearchMattersNextPageNumber(page);
  }
  return {
    meta,
    data: matters.map(
      (matter): ReportsOpenMattersReport => ({
        dateCreated: matter.dateCreated,
        dateModified: matter.dateModified,
        claimTypes: matter.claimTypes ?? [],
        openMattersReportMatterId: matter.id,
        openMattersReportMatterTitle: matter.title ?? undefined,
        openMattersReportMatterDateOfLoss: matter.dateOfLoss ?? undefined,
        openMattersReportMatterStatusLabel: matter.status?.label,
        openMattersReportMatterStatusCategory: matter.status?.categoryId,
        openMattersReportIncidentTypeLabel: getIncidentTypeLabel(
          matter.incidentType,
        ),
        openMattersReportMatterIncidentState:
          matter.incidentAddressState ?? undefined,
        openMattersReportMatterAssignee: (matter.assignees ?? [])
          .filter((assignee) => !assignee.dateTimeEnd)
          .map((assignee) => assignee.user.id),
        openMattersReportMatterTeam: matter.teams ?? [],
        openMattersReportMatterTag: matter.tags ?? [],
        openMattersReportMatterExpenseTotal:
          matter.expenseTotal == null ? undefined : Number(matter.expenseTotal),
        openMattersReportMedicalTreatmentBillTotal:
          matter.medicalTreatmentBillTotal == null
            ? undefined
            : Number(matter.medicalTreatmentBillTotal),
        openMattersReportMatterStatuteOfLimitations: (
          matter.statutesOfLimitations ?? []
        ).map((statute) => ({
          matterStatuteOfLimitationsId: statute.id,
          matterStatuteOfLimitationsDate: statute.date,
          matterStatuteOfLimitationsConfirmed: statute.confirmed,
          matterStatuteOfLimitationsDescription:
            statute.description ?? undefined,
        })),
        openMattersReportMatterDefendantNames: matter.defendantNames,
        openMattersReportclaimNumbers: matter.insuranceClaimNumbers,
        openMattersReportpolicyNumbers: matter.insurancePolicyNumbers,
        openMattersReportDaysInMatterStatus: matter.daysInStatus ?? undefined,
      }),
    ),
  } satisfies APIResponse<ReportsOpenMattersReport[]>;
};
